import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Input,
  Button,
  List,
  Typography,
  Space,
  Avatar,
  Spin,
  message,
  Popconfirm,
  Tooltip,
  Divider,
  Tag,
} from 'antd';
import {
  SendOutlined,
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useGetIdentity } from '@refinedev/core';
import { api } from '../../utils/axios';
import { useUserAvatarRender } from '../../hooks';

const { Text } = Typography;
const { TextArea } = Input;

export interface ChatMessage {
  id: number;
  text: string;
  relative_id: number;
  type: string;
  created_by: number;
  created_at: string;
  updated_at: string;
  creator?: {
    first_name?: string;
    last_name?: string;
  };
  updater?: {
    first_name?: string;
    last_name?: string;
  };
}

interface ChatComponentProps {
  type: string;
  relativeId: number;
  patientId?: number; // Optional patient ID to determine patient context
  title?: string;
  placeholder?: string;
  height?: string;
  isintialScroll?: boolean;
}

export const ChatComponent: React.FC<ChatComponentProps> = ({
  type,
  relativeId,
  patientId,
  title = 'Messages',
  placeholder = 'Type your message...',
  height = 'calc(100vh - 280px)',
  isintialScroll = false,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [editing, setEditing] = useState(false);
  const [currentDateSeparator, setCurrentDateSeparator] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const editInputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Use the avatar render hook
  const renderUserAvatar = useUserAvatarRender('small');

  // Group messages by date
  const groupMessagesByDate = (messages: ChatMessage[]) => {
    const groups: { [key: string]: ChatMessage[] } = {};
    messages.forEach(msg => {
      const date = dayjs(msg.created_at).format('YYYY-MM-DD');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(msg);
    });
    return groups;
  };

  // Handle scroll to update date separator
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;

    const container = messagesContainerRef.current;
    const scrollTop = container.scrollTop;

    // Don't show sticky date when at the very top (within first 50px)
    if (scrollTop < 50) {
      setCurrentDateSeparator('');
      return;
    }

    // Find which message is currently visible at the top of the container
    const messageElements = container.querySelectorAll('[data-message-date]');
    let visibleDate = '';

    for (let i = 0; i < messageElements.length; i++) {
      const element = messageElements[i] as HTMLElement;
      const rect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Check if this message is visible at the top of the container
      if (rect.top <= containerRect.top + 80) {
        // 80px threshold
        visibleDate = element.getAttribute('data-message-date') || '';
      } else {
        break;
      }
    }

    // Only set the date if it's different from what's already showing
    if (visibleDate && visibleDate !== currentDateSeparator) {
      setCurrentDateSeparator(visibleDate);
    }
  };

  // Scroll to bottom of messages within the chat container only
  const scrollToBottom = () => {
    if (!isintialScroll || !messagesContainerRef.current) {
      return;
    }

    const container = messagesContainerRef.current;
    container.scrollTop = container.scrollHeight;
  };

  // Fetch messages
  const fetchMessages = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }

      // Don't fetch if relativeId is not available
      if (!relativeId) {
        setMessages([]);
        return;
      }

      // Convert type to uppercase to match API expectations
      const apiType = type.toUpperCase();
      let apiUrl = '';

      // Implement conditional logic based on requirements
      if (apiType === 'LEAD') {
        // If type is LEAD: type_in=LEAD,L_FOLLOW_UP
        const types = ['LEAD', 'L_FOLLOW_UP'];
        apiUrl = `/chat-messages?type_in=${types.join(',')}&relative_id=${relativeId}&sort=created_at&order=asc`;
      } else if (apiType === 'L_FOLLOW_UP') {
        // If type is L_FOLLOW_UP: type_in=LEAD,L_FOLLOW_UP
        const types = ['LEAD', 'L_FOLLOW_UP'];
        apiUrl = `/chat-messages?type_in=${types.join(',')}&relative_id=${relativeId}&sort=created_at&order=asc`;
      } else if (apiType === 'APPOINTMENT' && (!patientId || patientId === 0)) {
        // If type is APPOINTMENT and patient_id is 0 or not provided: type_in=APPOINTMENT,A_FOLLOW_UP
        const types = ['APPOINTMENT', 'A_FOLLOW_UP'];
        apiUrl = `/chat-messages?type_in=${types.join(',')}&relative_id=${relativeId}&sort=created_at&order=asc`;
      } else if (apiType === 'A_FOLLOW_UP' && (!patientId || patientId === 0)) {
        // If type is A_FOLLOW_UP and patient_id is 0 or not provided: type_in=APPOINTMENT,A_FOLLOW_UP
        const types = ['APPOINTMENT', 'A_FOLLOW_UP'];
        apiUrl = `/chat-messages?type_in=${types.join(',')}&relative_id=${relativeId}&sort=created_at&order=asc`;
      } else if (
        apiType === 'APPOINTMENT' ||
        apiType === 'PATIENT' ||
        apiType === 'A_FOLLOW_UP' ||
        apiType === 'P_FOLLOW_UP' ||
        apiType === 'FEEDBACK'
      ) {
        // If type is APPOINTMENT, PATIENT, or any follow-up type with patient_id exists: type_in=APPOINTMENT,PATIENT,A_FOLLOW_UP,P_FOLLOW_UP,T_FOLLOW_UP
        const types = [
          'APPOINTMENT',
          'PATIENT',
          'A_FOLLOW_UP',
          'P_FOLLOW_UP',
          'FEEDBACK',
        ];
        apiUrl = `/chat-messages?type_in=${types.join(',')}&relative_id=${relativeId}&sort=created_at&order=asc`;
      } else {
        // Fallback for other types (should not happen based on current usage)
        const types = ['LEAD', 'L_FOLLOW_UP'];
        apiUrl = `/chat-messages?type_in=${types.join(',')}&relative_id=${relativeId}&sort=created_at&order=asc`;
      }

      const response = await api.get<ChatMessage[] | { data: ChatMessage[] }>(
        apiUrl
      );

      // Handle both array response and wrapped data response
      const messagesData = Array.isArray(response)
        ? response
        : response.data || response;
      setMessages(Array.isArray(messagesData) ? messagesData : []);
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      if (showLoading) {
        message.error('Failed to load messages');
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || sending || !relativeId) return;

    try {
      setSending(true);
      // Send type in uppercase to match API expectations
      const messageData = {
        text: newMessage.trim(),
        type: type.toUpperCase(),
        relative_id: relativeId,
      };

      const response = await api.post<ChatMessage>(
        '/chat-messages',
        messageData
      );

      // Clear the input first
      setNewMessage('');

      // Refetch all messages silently (no loading spinner)
      await fetchMessages(false);

      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Failed to send message:', error);
      message.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Start editing message
  const startEditing = (msg: ChatMessage) => {
    setEditingMessageId(msg.id);
    // Handle multiple levels of escaping by recursively replacing \\n with \n
    let processedText = msg.text;
    while (processedText.includes('\\n')) {
      processedText = processedText.replace(/\\n/g, '\n');
    }
    setEditingText(processedText);
    setEditing(true);
    // Focus the edit input after a short delay
    setTimeout(() => {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }, 100);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingMessageId(null);
    setEditingText('');
    setEditing(false);
  };

  // Save edited message
  const saveEditedMessage = async () => {
    if (!editingText.trim()) return;

    try {
      setEditing(true);
      const messageToEdit = messages.find(msg => msg.id === editingMessageId);
      if (!messageToEdit) return;

      const response = await api.put<ChatMessage>(
        `/chat-messages/${editingMessageId}`,
        {
          text: editingText.trim(),
          type: messageToEdit.type,
          relative_id: messageToEdit.relative_id,
        }
      );

      // Update the message locally
      setMessages(prev =>
        prev.map(msg =>
          msg.id === editingMessageId
            ? {
                ...msg,
                text: editingText.trim(),
                updated_at: new Date().toISOString(),
              }
            : msg
        )
      );

      // Exit edit mode
      cancelEditing();
      message.success('Message updated successfully');
    } catch (error) {
      console.error('Failed to update message:', error);
      message.error('Failed to update message');
    } finally {
      setEditing(false);
    }
  };

  // Delete message
  const deleteMessage = async (messageId: number) => {
    try {
      await api.delete(`/chat-messages/${messageId}`);

      // Remove message from local state
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      message.success('Message deleted successfully');
    } catch (error) {
      console.error('Failed to delete message:', error);
      message.error('Failed to delete message');
    }
  };

  // Handle key press for new message
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle key press for editing
  const handleEditKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveEditedMessage();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  // Get sender name
  const getSenderName = (msg: ChatMessage) => {
    if (msg.creator?.first_name && msg.creator?.last_name) {
      return `${msg.creator.first_name} ${msg.creator.last_name}`;
    }
    return msg.creator?.first_name || 'Unknown User';
  };

  // Get current user identity
  const { data: currentUser } = useGetIdentity();

  // Check if current user can edit/delete the message
  const canModifyMessage = (msg: ChatMessage) => {
    // System messages (created_by is null) cannot be modified
    if (msg.created_by === null) {
      return false;
    }
    
    // Only the message creator can edit/delete their message
    return (currentUser as any)?.id === msg.created_by;
  };

  // Load messages on mount or when props change
  useEffect(() => {
    if (relativeId) {
      fetchMessages();
    }
  }, [type, relativeId]);

  // Initialize scroll handler and reset date separator when messages change
  useEffect(() => {
    setCurrentDateSeparator('');
    if (messagesContainerRef.current) {
      handleScroll();
    }
  }, [messages]);

  return (
    <Card
      style={{
        height,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      styles={{
        body: {
          padding: '12px',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* Messages List */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          marginBottom: 16,
          backgroundColor: '#fafafa',
          borderRadius: '6px',
          padding: '8px',
          scrollBehavior: 'smooth',
          position: 'relative',
        }}
      >
        {/* Date Separator - WhatsApp style - only show when scrolling and not at the very top */}
        {currentDateSeparator && messages.length > 0 && (
          <div
            style={{
              position: 'sticky',
              top: '8px',
              zIndex: 10,
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '8px',
              pointerEvents: 'none', // Prevent interaction with the sticky element
            }}
          >
            <div
              style={{
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                padding: '4px 8px',
                borderRadius: '16px',
                fontSize: '9px',
                fontWeight: 400,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: '1px solid #bbdefb',
              }}
            >
              {dayjs(currentDateSeparator).format('MMMM DD, YYYY')}
            </div>
          </div>
        )}
        {loading ? (
          <div
            style={{ display: 'flex', justifyContent: 'center', padding: 24 }}
          >
            <Spin size="large" />
          </div>
        ) : messages.length === 0 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#999',
            }}
          >
            No messages yet. Start the conversation!
          </div>
        ) : (
          <div>
            {Object.entries(groupMessagesByDate(messages)).map(
              ([date, dateMessages]) => (
                <div key={date}>
                  {/* Date Header */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      margin: '16px 0 8px 0',
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        padding: '4px 8px',
                        borderRadius: '16px',
                        fontSize: '9px',
                        fontWeight: 400,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: '1px solid #bbdefb',
                      }}
                    >
                      {dayjs(date).format('MMMM DD, YYYY')}
                    </div>
                  </div>

                  {/* Messages for this date */}
                  {dateMessages.map((msg, index) => {
                    // Check if this is a system message (created_by is null)
                    const isSystemMessage = msg.created_by === null;
                    
                    // Check if this is current user's message
                    const isCurrentUserMessage = (currentUser as any)?.id === msg.created_by;
                    
                    // For system messages, show only message text (no type tag)
                    if (isSystemMessage) {
                      return (
                        <div
                          key={msg.id}
                          data-message-date={date}
                          style={{
                            marginBottom: '8px',
                            padding: '8px 0',
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <Tag
                            color="default"
                            style={{
                              fontSize: '11px',
                              borderRadius: '16px',
                              padding: '4px 12px',
                              margin: 0,
                              border: 'none',
                              fontWeight: 500,
                              backgroundColor: '#f5f5f5',
                              color: '#666',
                            }}
                          >
                            {msg.text}
                          </Tag>
                        </div>
                      );
                    }

                    // For user messages, show full message with proper alignment
                    return (
                      <div
                        key={msg.id}
                        data-message-date={date}
                        style={{
                          marginBottom: '8px',
                          padding: '8px 0',
                          display: 'flex',
                          justifyContent: isCurrentUserMessage ? 'flex-end' : 'flex-start',
                        }}
                      >
                        <div
                          style={{
                            maxWidth: '70%',
                            backgroundColor: isCurrentUserMessage ? '#1890ff' : '#fff',
                            padding: '12px 16px',
                            borderRadius: isCurrentUserMessage 
                              ? '18px 18px 4px 18px' 
                              : '18px 18px 18px 4px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            border: isCurrentUserMessage 
                              ? 'none' 
                              : '1px solid #f0f0f0',
                          }}
                        >
                          {/* Message header with user info and timestamp */}
                          {!isCurrentUserMessage && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '8px',
                              }}
                            >
                              {renderUserAvatar(
                                msg?.updater?.first_name
                                  ? msg?.updater
                                  : msg?.creator
                              )}
                              <div>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                  }}
                                >
                                  <Tag
                                    color="purple"
                                    style={{
                                      fontSize: '9px',
                                      borderRadius: '12px',
                                      padding: '1px 6px',
                                      margin: 0,
                                      border: 'none',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {msg.type}
                                  </Tag>
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: '10px' }}
                                  >
                                    {dayjs(msg.created_at).format('HH:mm')}
                                    {msg.updated_at !== msg.created_at && ' (edited)'}
                                  </Text>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Message header with type for current user's messages */}
                          {isCurrentUserMessage && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '8px',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <Tag
                                  color="purple"
                                  style={{
                                    fontSize: '9px',
                                    borderRadius: '12px',
                                    padding: '1px 6px',
                                    margin: 0,
                                    border: 'none',
                                    fontWeight: 500,
                                  }}
                                >
                                  {msg.type}
                                </Tag>
                                <Text
                                  type="secondary"
                                  style={{ 
                                    fontSize: '10px',
                                    color: 'rgba(255,255,255,0.8)',
                                  }}
                                >
                                  {dayjs(msg.created_at).format('HH:mm')}
                                  {msg.updated_at !== msg.created_at && ' (edited)'}
                                </Text>
                              </div>
                            </div>
                          )}

                          {/* Message content */}
                          {editingMessageId === msg.id ? (
                            <div style={{ marginTop: '8px' }}>
                              <TextArea
                                ref={editInputRef}
                                value={editingText}
                                onChange={e => setEditingText(e.target.value)}
                                onKeyPress={handleEditKeyPress}
                                autoSize={{ minRows: 1, maxRows: 3 }}
                                style={{ marginBottom: '8px' }}
                              />
                              <Space size="small">
                                <Button
                                  type="primary"
                                  size="small"
                                  onClick={saveEditedMessage}
                                >
                                  Save
                                </Button>
                                <Button size="small" onClick={cancelEditing}>
                                  Cancel
                                </Button>
                              </Space>
                            </div>
                          ) : (
                            <div
                              onDoubleClick={() =>
                                canModifyMessage(msg) && startEditing(msg)
                              }
                              style={{
                                cursor: canModifyMessage(msg)
                                  ? 'pointer'
                                  : 'default',
                                userSelect: 'none',
                                marginTop: '4px',
                              }}
                              title={
                                canModifyMessage(msg)
                                  ? 'Double click to edit'
                                  : ''
                              }
                            >
                              <Text
                                style={{
                                  whiteSpace: 'pre-wrap',
                                  fontSize: '13px',
                                  color: isCurrentUserMessage ? '#fff' : '#333',
                                  lineHeight: '1.4',
                                  display: 'block',
                                }}
                              >
                                {(() => {
                                  // Handle multiple levels of escaping by recursively replacing \\n with \n
                                  let processedText = msg.text;
                                  while (processedText.includes('\\n')) {
                                    processedText = processedText.replace(
                                      /\\n/g,
                                      '\n'
                                    );
                                  }
                                  return processedText;
                                })()}
                              </Text>
                            </div>
                          )}

                          {/* Message actions for current user's messages */}
                          {isCurrentUserMessage && canModifyMessage(msg) && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                marginTop: '8px',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <Space size="small">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => startEditing(msg)}
                                  style={{
                                    padding: '2px 4px',
                                    minWidth: 'auto',
                                    color: 'rgba(255,255,255,0.8)',
                                  }}
                                  title="Double click to edit"
                                />
                                <Popconfirm
                                  title="Delete this message?"
                                  description="This action cannot be undone."
                                  onConfirm={() => deleteMessage(msg.id)}
                                  okText="Yes"
                                  cancelText="No"
                                >
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    style={{
                                      padding: '2px 4px',
                                      minWidth: 'auto',
                                      color: 'rgba(255,255,255,0.8)',
                                    }}
                                    title="Delete message"
                                  />
                                </Popconfirm>
                              </Space>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div style={{ flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
          <TextArea
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            placeholder={placeholder}
            onKeyPress={handleKeyPress}
            autoSize={{ minRows: 1, maxRows: 3 }}
            style={{ flex: 1 }}
            disabled={sending}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={sendMessage}
            loading={sending}
            disabled={!newMessage.trim() || sending || !relativeId}
            style={{ flexShrink: 0 }}
          >
            Send
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ChatComponent;
